/**
 * http request widget
 *
 * request options:
 * {
 *   name: api id,
 *   url: request url,
 *   data: request data,
 *   method: request method,
 *   headers: request headers,
 *   params: params set for uri
 * }
 */
import axios from 'axios';
import querystring from 'querystring';

const { pathToRegexp } = require('path-to-regexp');

const protocol = window.location.protocol;

// 配置默认请求地址
axios.defaults.baseURL = `${protocol}//api.${process.env.VUE_APP_DOMAIN}/v4`;
axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded'; // 配置默认请求头
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // 拦截响应res,并做一些错误处理
    if (error && error.response) {
      switch (error.response.status) {
        case 400:
          error.message = '找不到页面(400)';
          break;
        case 401:
          error.message = '未授权，请重新登录(401)';
          break;
        case 403:
          error.message = '拒绝访问(403)';
          break;
        case 404:
          error.message = '请求出错(404)';
          break;
        case 408:
          error.message = '请求超时(408)';
          break;
        case 500:
          error.message = '服务器错误(500)';
          break;
        case 501:
          error.message = '服务未实现(501)';
          break;
        case 502:
          error.message = '网络错误(502)';
          break;
        case 503:
          error.message = '服务不可用(503)';
          break;
        case 504:
          error.message = '网络超时(504)';
          break;
        case 505:
          error.message = 'HTTP版本不受支持(505)';
          break;
        default:
          error.message = `连接出错(${error.response.status})!`;
      }
    } else {
      error.message = '连接服务器出错';
    }

    return Promise.reject(error); // 返回请求失败错误信息
  }
);

/**
 * http request class
 */
export default class Http {
  constructor(apis) {
    this.apis = apis;

    return this;
  }

  install(Vue) {
    const self = this;
    Vue.prototype.$http = {
      get(options) {
        const newOptions = Object.assign(options, {
          method: 'GET',
        });

        return request(newOptions);
      },

      post(options) {
        const newOptions = Object.assign(options, {
          method: 'POST',
        });
        return request(newOptions);
      },

      patch(options) {
        const newOptions = Object.assign(options, {
          method: 'PATCH',
        });

        return request(newOptions);
      },

      put(options) {
        const newOptions = Object.assign(options, {
          method: 'PUT',
        });

        return request(newOptions);
      },

      delete(options) {
        const newOptions = Object.assign(options, {
          method: 'DELETE',
        });

        return request(newOptions);
      },
    };

    /**
     * Send http request
     * @param  {Object}   options  request options
     */
    function request(options) {
      const { name, url, data, method, headers, params } = options;
      let api = getApi(name, method, data, params);
      let uri = api.uri;

      // if (uri.indexOf('?') > -1) {
      //   uri += '&app=H5';
      // } else {
      //   uri += '?app=H5';
      // }

      const sOptions = {
        method: (method && method.toUpperCase()) || 'GET',
        url: url || uri,
        headers: headers || {},
      };

      let request_id = (() => {
        const uuid = 'uuid xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          const r = (Math.random() * 16) | 0,
            v = c == 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
        return uuid;
      })();
      sOptions.headers['X-Request-Id'] = request_id;

      if (sOptions.method !== 'GET' && data && !sOptions.headers['content-type']) {
        sOptions.data = querystring.stringify(data);
      } else {
        sOptions.data = data;
      }

      if (api.auth === 'required' || api.auth === 'optional') {
        const queryString = querystring.parse(window.location.search);
        let token = queryString.token || queryString['?token'];

        const user = Vue.prototype.$jwt.user();
        if (!token && user !== null) {
          token = user.token;
        }

        if (token) {
          sOptions.headers['Authorization'] = `Bearer ${token}`;
        } else {
          if (api.auth === 'required') {
            return Promise.reject({
              message: 'Unauthorized request',
            });
          }
        }
      }

      return axios(sOptions);
    }

    /**
     * Get api object by name. URI will be replaced by data and params
     * @param  {String} name    Api name
     * @param  {String} method  Request method
     * @param  {Object} data    Request data
     * @param  {Object} params  Request params
     * @return {Object}
     */
    function getApi(name, method, data, params) {
      if (!name) {
        return '';
      }
      let api = self.apis[name];
      let uri = api.uri;

      let keys = [];
      pathToRegexp(uri, keys);

      if (keys.length > 0) {
        keys.forEach((key) => {
          if (!params[key.name]) {
            throw new Error(
              `API name: ${name}. You are using dynamic params but ${key.name} not existed in your params`
            );
          }

          uri = uri.replace(`:${key.name}`, params[key.name] || 'undefined');
        });
      }

      if (method === 'GET' && data) {
        uri += '?' + querystring.stringify(data);
      }

      return {
        uri,
        auth: api.auth,
      };
    }
  }
}